import { useEffect, useRef } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import Card from "../shared/Card/Card";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import ScanTimerToggle from "../shared/ScanTimerToggle";
import useDepollutionTimers from "../../hooks/useDepollutionTimers";
import formatDateTime from "../../utils/formatDateTime";

interface DepollutionTimerCardProps {
  depollutionId?: string;
  /** The logged-in user, required to pause or finish a session. */
  userId?: string;
  /**
   * Start the timer once when the card mounts (if none is already running).
   * Used when arriving here via a vehicle scan on the Depollution list.
   */
  autoStart?: boolean;
  /**
   * When true, scanning a vehicle toggles the timer (start when idle, finish
   * when running), mirroring JobWorkspace. Enable only on the standalone page,
   * not when embedded in a page that owns the scanner.
   */
  enableScanToggle?: boolean;
  /**
   * Called after the timer finishes successfully. Used on the standalone page
   * to return to the list ready for the next vehicle scan.
   */
  onSessionFinished?: () => void;
  /** Passed through to the Card wrapper, e.g. to stretch it in a grid row. */
  containerClassName?: string;
}

export default function DepollutionTimerCard({
  depollutionId,
  userId,
  autoStart = false,
  enableScanToggle = false,
  onSessionFinished,
  containerClassName,
}: DepollutionTimerCardProps) {
  const { t } = useTranslation("vehicles");
  const {
    totalElapsedSeconds,
    hasActiveSession,
    firstStartedAt,
    lastActivityAt,
    isSessionsLoading,
    isSessionActionLoading,
    handleStart,
    handlePause,
    handleFinish,
  } = useDepollutionTimers(depollutionId, onSessionFinished);

  // Auto-start the timer on a scan-in, once sessions have loaded and only if
  // one isn't already running. The ref keeps it to a single start per mount.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStartedRef.current) return;
    if (!depollutionId || isSessionsLoading) return;
    autoStartedRef.current = true;
    if (!hasActiveSession) handleStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, depollutionId, isSessionsLoading, hasActiveSession]);

  const formatDuration = (seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(safeSeconds / 3600).toString().padStart(2, "0");
    const minutes = Math.floor((safeSeconds % 3600) / 60).toString().padStart(2, "0");
    const remainingSeconds = Math.floor(safeSeconds % 60).toString().padStart(2, "0");

    return `${hours}:${minutes}:${remainingSeconds}`;
  };

  return (
    <Card title={t("Depollution Timer")} subtitle={t("Start, pause and finish the depollution timer. Totals include the active session.")} bodyClassName="p-4" containerClassName={containerClassName}>
      {enableScanToggle && (
        <ScanTimerToggle
          userId={userId}
          hasActiveSession={hasActiveSession}
          isSessionsLoading={isSessionsLoading}
          isSessionActionLoading={isSessionActionLoading}
          onStart={handleStart}
          onFinish={handleFinish}
        />
      )}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="text-left">
          <p className="font-mono text-5xl font-semibold tracking-tight text-primary-600 lg:text-6xl">
            {formatDuration(totalElapsedSeconds)}
          </p>
          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            {hasActiveSession ? t("Session active") : t("Not running")}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {hasActiveSession ? (
              <>
                <ButtonInput
                  label={t("Pause")}
                  isSubmit={false}
                  classes="!bg-amber-600 hover:!bg-amber-700"
                  isLoading={isSessionActionLoading}
                  disabled={!userId}
                  onClick={() => userId && handlePause(userId)}
                />
                <ButtonInput
                  label={t("Finish")}
                  isSubmit={false}
                  classes="!bg-green-600 hover:!bg-green-700"
                  isLoading={isSessionActionLoading}
                  disabled={!userId}
                  onClick={() => userId && handleFinish(userId)}
                />
              </>
            ) : (
              <ButtonInput
                label={t("Start")}
                isSubmit={false}
                classes=""
                isLoading={isSessionActionLoading}
                disabled={!depollutionId}
                onClick={() => handleStart()}
              />
            )}
          </div>
        </div>

        <div className="flex w-full flex-col gap-3 lg:w-80">
          <div className="rounded-md bg-white px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Started")}</p>
            <p className="text-sm text-gray-700">
              {firstStartedAt
                ? formatDateTime(firstStartedAt, DateTime.DATETIME_SHORT)
                : "-"}
            </p>
          </div>
          <div className="rounded-md bg-white px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Last Activity")}</p>
            <p className="text-sm text-gray-700">
              {lastActivityAt
                ? formatDateTime(lastActivityAt, DateTime.DATETIME_SHORT)
                : "-"}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
